import { useCustomQuery } from "@/hooks/useCustomQuery"
import DetailAvatar from "@/components/Avatar/DetailAvatar"
import LetterAvatar from "@/components/Avatar/LetterAvatar"
import { DetailTable } from "@/components/Tables/DetailTable"
import { columns } from "@/pages/orders/DetailColumns"
import { CrudProvider, useCrudContext } from "@/context/crudContext"

import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Divider,
} from "@mui/material"
import { useParams, Link } from "react-router-dom"
import { useState, useEffect } from "react"
import Loading from "@/components/Loading"
import { formatToDevanagariStyle } from "@/utilities/helper"
import StarIcon from "@mui/icons-material/Star"
import { isAdminPortal } from "@/utilities/domain"

const ProductDetails = () => {
  const { id } = useParams()

  const {
    data: response,
    isLoading,
    isError,
    error,
  } = useCustomQuery(["product-detail", id], `product/${id}`)

  if (isLoading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Loading />
      </div>
    )
  }

  const product = response?.data

  return (
    <div>
      {/* Breadcrumb */}
      <div className="breadcrumb-card">
        <h5>Product Details</h5>
        <ul className="breadcrumb text-sm">
          <li>
            <Link to="/pos/dashboard">
              <i className="material-symbols-outlined">home</i> Dashboard
            </Link>
          </li>
          <li>
            <Link to="/pos/products">Products</Link>
          </li>
          <li>{product.code}</li>
        </ul>
      </div>

      {product ? (
        <div className="grid grid-cols-1 lg:grid-cols-4 xxl:grid-cols-5 gap-6">
          {/* Left Panel - Product Info */}
          <div className="bg-white border-0 rounded-3 p-4 mb-4">
            <div className="flex items-center mb-6">
              {product.thumbnail ? (
                <img
                  src={product.thumbnail.url}
                  alt={product.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <LetterAvatar name={product?.name} />
              )}
            </div>

            <DetailItem label="Code" value={product.code} />
            <DetailItem label="SKU" value={product.sku} />
            {product.brand_name && (
              <DetailItem label="Brand" value={product.brand_name} />
            )}
            <div className="mb-3">
              <span className="block text-sm text-gray-600 mb-1">Tags:</span>
              <div className="flex flex-wrap gap-2">
                {Array.isArray(product.tags)
                  ? product.tags.map((tag, index) => (
                      <TagsBadge key={tag.id || index} name={tag?.name} />
                    ))
                  : product.tags?.name && (
                      <TagsBadge name={product.tags.name} />
                    )}
              </div>
            </div>
            <DetailItem label="Item Stock" value={product.stock} />
            <DetailItem
              label="Price"
              value={`Rs ${formatToDevanagariStyle(product.sell_price)}`}
            />
            <DetailItem label="Supplier" value={product.supplier?.name} />
            <DetailItem label="Updated At" value={product.updated_at} />
            <DetailItem label="Created At" value={product.created_at} />
            <DetailItem label="Created By" value={product.createdBy?.name} />
            <DetailItem label="Updated By" value={product.updatedBy?.name} />
          </div>

          {/* Right Panel - Images and Orders */}
          <div className="lg:col-span-3 xxl:col-span-4 space-y-6">
            {isAdminPortal() && (
              <BranchStockBreakdown
                stocks={product.branch_stock_breakdown ?? []}
              />
            )}
            {/* Product Images */}
            {product.images && product.images.length > 0 && (
              <div className="card bg-white border-0 px-4 py-5 rounded-3">
                <p className="mb-5 text-base font-semibold text-black">
                  Product Images
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {product.images.map((image) => (
                    <div
                      key={image.id}
                      className={`relative border-2 rounded-lg overflow-hidden aspect-square ${
                        image.is_thumbnail
                          ? "border-blue-500"
                          : "border-gray-200"
                      }`}
                    >
                      <img
                        src={image.url}
                        alt={image.original_name || product.name}
                        className="w-full h-full object-cover"
                      />
                      {image.is_thumbnail && (
                        <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1">
                          <StarIcon sx={{ fontSize: 16 }} />
                        </div>
                      )}
                      {image.is_thumbnail && (
                        <div className="absolute bottom-0 left-0 right-0 bg-blue-500 text-white text-center py-1 text-xs">
                          Thumbnail
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Orders */}
            <div className="card bg-white border-0 px-4 py-5 rounded-3">
              <p className="mb-5 text-base font-semibold text-black">
                Recent Orders
              </p>
              <CrudProvider>
                <OrderTable product={product} isLoading={isLoading} />
              </CrudProvider>
            </div>
          </div>
        </div>
      ) : (
        <div>No product data found.</div>
      )}
    </div>
  )
}

function BranchStockBreakdown({ stocks }) {
  const physical = stocks.reduce((total, stock) => total + stock.quantity, 0)
  const reserved = stocks.reduce(
    (total, stock) => total + stock.reserved_quantity,
    0
  )

  return (
    <div className="card bg-white border-0 px-4 py-5 rounded-3">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-base font-semibold text-black mb-0">Branch Stock</p>
        <div className="text-sm text-gray-600 flex gap-4">
          <span>
            Physical: <strong>{physical}</strong>
          </span>
          <span>
            Reserved: <strong>{reserved}</strong>
          </span>
          <span>
            Available: <strong>{physical - reserved}</strong>
          </span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-gray-600 border-b">
            <tr>
              <th className="pb-2">Branch</th>
              <th className="pb-2 text-right">Physical</th>
              <th className="pb-2 text-right">Reserved</th>
              <th className="pb-2 text-right">Available</th>
            </tr>
          </thead>
          <tbody>
            {stocks.map((stock) => (
              <tr key={stock.branch_id} className="border-b last:border-0">
                <td className="py-3">
                  {stock.branch_name}{" "}
                  {stock.branch_code ? `(${stock.branch_code})` : ""}
                  {!stock.is_active && (
                    <span className="ml-2 text-xs text-red-500">Inactive</span>
                  )}
                </td>
                <td className="py-3 text-right">{stock.quantity}</td>
                <td className="py-3 text-right">{stock.reserved_quantity}</td>
                <td className="py-3 text-right font-medium">
                  {stock.available_quantity}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function OrderTable({ product, isLoading }) {
  return (
    <DetailTable
      label="Orders"
      columns={columns}
      data={product.orders}
      isLoading={isLoading}
    />
  )
}

function TagsBadge({ name }) {
  return (
    <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-indigo-100 text-indigo-800 text-xs font-medium capitalize">
      {name}
    </span>
  )
}

function DetailItem({ label, value }) {
  return (
    <div className="mb-3">
      <p className="text-sm text-gray-600">{label}:</p>
      <span className="text-sm font-medium text-gray-900 break-words">
        {value}
      </span>
    </div>
  )
}

export default ProductDetails
